import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError, type ApiClient } from '../api/client';
import { ConversationActivitySidebar } from '../components/ConversationActivitySidebar';
import {
  ConversationComposer,
  ConversationHeader
} from '../components/ConversationChrome';
import { ConversationMessageList } from '../components/ConversationMessageList';
import {
  ConversationSecretsModal,
  ConversationToolAccessModal
} from '../components/ConversationModals';
import { Dialog } from '../components/Dialog';
import { InteractiveAgentFeed } from '../components/InteractiveAgentFeed';
import { SessionTerminalPanel } from '../components/SessionTerminalPanel';
import ErrorBanner from '../components/ErrorBanner';
import { FixBugDialog, FIX_BUG_WORKFLOW_INSTRUCTIONS } from '../components/FixBugDialog';
import type {
  ApprovalRequest,
  AutopilotMode,
  AppGitlabInfo,
  AppItem,
  CodexRun,
  Conversation,
  ConversationAttachment,
  ConversationAccessSummary,
  ConversationAppSecret,
  ConversationAppSecretsResponse,
  ConversationAutopilotSettings,
  ConversationGitlabInfo,
  ConversationMessage,
  ConversationProjection,
  CreateMessageResponse,
  CreateConversationAppSecretRequest,
  ExecGrant,
  ExecGrantCreateResponse,
  ExecGrantListResponse,
  MessageStatus,
  MergeRequestSummary,
  ConversationModelOption,
  MlSandbox,
  MlSandboxRun,
  OpsOverview,
  OpsSecret,
  QueueItem,
  RunEvent,
  RunStatus,
  Session,
  ToolApproval,
  ToolTokenResponse,
  ToolTokenStatus,
  UpdateMessageResponse
} from '../types';
import {
  CONVERSATION_CAPABILITIES,
  conversationControlAccess,
  hasConversationCapability
} from '../utils/conversationAccess';
import { DEFAULT_TOOL_SCOPES, TOOL_SCOPES } from '../constants';
import {
  clearServerDraft,
  loadDraft,
  loadServerDraft,
  saveDraft,
  saveServerDraft
} from '../utils/drafts';
import type { ConversationInputDraft, ConversationInputDraftVersion } from '../utils/drafts';
import { reportError, reportErrorLocal } from '../utils/errors';
import {
  EXEC_GRANT_DEFAULT_DURATION,
  parseExecutionGrantDuration,
  type ExecutionGrantDurationUnit
} from '../utils/executionGrantDuration';
import { conversationAuthorizationView } from '../utils/conversationAuthorization';
import { sortConversationMessagesForDisplay } from '../utils/conversationThread';
import { createForegroundRevalidationGate } from '../utils/foregroundRevalidation';
import { buildCreateMessageRequest, resolveComposerParentMessageId } from '../utils/composer';
import { resolveInitialConversationModelId } from '../utils/conversationModelSelection';
import {
  compareConversationMessages,
  normalizeUpdatePayload,
  parseApprovalRequestFromPayload,
  RunnerState
} from '../utils/conversationRunActivity';
import { useConversationActivitySidebar } from '../hooks/useConversationActivitySidebar';
import { useConversationDerivedState } from '../hooks/useConversationDerivedState';
import { useConversationPageActions } from '../hooks/useConversationPageActions';
import { useConversationPageEffects } from '../hooks/useConversationPageEffects';
import { useConversationPageLoaders } from '../hooks/useConversationPageLoaders';
import { useDismissibleMenu } from '../hooks/useDismissibleMenu';
import { ToolBrowserActivity } from './toolBrowser/Activity';
import { ToolBrowserGrants } from './toolBrowser/Grants';
import './ConversationPage.lane51.css';

const RUNNER_PHASE_PROGRESS: Record<string, [number, number]> = {
  init: [0, 8],
  build: [8, 30],
  validate: [30, 38],
  workspace: [38, 65],
  setup: [65, 80],
  run: [80, 94],
  cleanup: [94, 100],
  complete: [100, 100]
};

const MOBILE_LAYOUT_QUERY = '(max-width: 720px)';
const RUNNER_SIDEBAR_WIDTH_STORAGE_KEY = 'conversation.runnerSidebarWidth';
const LAST_SELECTED_MODEL_STORAGE_KEY = 'conversation.lastSelectedModelId';
const RUNNER_SIDEBAR_MIN_WIDTH = 280;
const RUNNER_THREAD_MIN_WIDTH = 360;
const RUNNER_SIDEBAR_DEFAULT_WIDTH_RATIO = 0.38;
const ACTIVITY_STAGES_PAGE = 12;
const ACTIVITY_STAGE_COMMANDS_PAGE = 40;
const ACTIVITY_OUTPUT_LINES_PAGE = 120;
const RUNNER_SIDEBAR_DEFAULT_WIDTH_FALLBACK = 420;
const TOOL_APPROVAL_POLL_ACTIVE_MS = 5000;
const TOOL_APPROVAL_POLL_IDLE_MS = 30000;
const TOOL_APPROVAL_POLL_HIDDEN_MS = 60000;
const SANDBOX_POLL_ACTIVE_MS = 8000;
const SANDBOX_POLL_IDLE_MS = 30000;
const SANDBOX_POLL_HIDDEN_MS = 60000;
const CONVERSATION_ACCESS_REFRESH_MS = 60000;
const DRAFT_SERVER_SAVE_DEBOUNCE_MS = 800;
const AUTOPILOT_MESSAGE_PRIORITY_MIN = 9;
const LEGACY_UNASSIGNED_COMMAND_ID = 'unassigned';
const FALLBACK_OUTPUT_COMMAND_ID = 'runner.output';
const FALLBACK_OUTPUT_STEP = 'Runner output';
const RUNNER_COMMAND_STEP_LABELS: Record<string, string> = {
  'runner.init': 'Init output',
  'runner.build': 'Build output',
  'runner.validate': 'Validation output',
  'runner.workspace': 'Workspace output',
  'runner.setup': 'Setup output',
  'runner.run': 'Run output',
  'runner.cleanup': 'Cleanup output',
  'runner.complete': 'Complete output',
  'runner.bootstrap': 'Bootstrap diagnostics',
  [FALLBACK_OUTPUT_COMMAND_ID]: FALLBACK_OUTPUT_STEP
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.onload = () => {
      const value = String(reader.result ?? '');
      resolve(value.includes(',') ? value.split(',').pop() ?? '' : value);
    };
    reader.readAsDataURL(file);
  });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function pageIsVisible() {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

function sandboxStatusIsActive(status: string | null | undefined) {
  return ['pending', 'building'].includes(status ?? '');
}

function sandboxRunStatusIsActive(status: string | null | undefined) {
  return ['pending', 'running'].includes(status ?? '');
}

function isConversationAuthorizationFailure(error: unknown): boolean {
  return error instanceof ApiError && [401, 403, 404].includes(error.status);
}

export default function ConversationPage({
  apiClient,
  setError
}: {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
}) {
  const params = useParams();
  const appId = params.appId ?? '';
  const conversationId = params.conversationId ?? '';
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [conversationAccess, setConversationAccess] = useState<ConversationAccessSummary | null>(
    null
  );
  const [conversationAccessLoading, setConversationAccessLoading] = useState(true);
  const [conversationAccessRefreshVersion, setConversationAccessRefreshVersion] = useState(0);
  const currentConversationAccess =
    conversationAccess?.conversation_id === conversationId ? conversationAccess : null;
  const conversationAuthorizationReady =
    !conversationAccessLoading &&
    hasConversationCapability(currentConversationAccess, CONVERSATION_CAPABILITIES.read);
  const authorizationView = conversationAuthorizationView(
    conversationAccessLoading,
    conversationAuthorizationReady
  );
  const ordinaryControls = conversationControlAccess(currentConversationAccess);
  const browserOperations = apiClient.mode === 'tool-browser' ? currentConversationAccess?.browser_operations : undefined;
  const canUseDelegatedExecutionGrants = apiClient.mode === 'tool-browser' &&
    apiClient.browserGrantManagement === true && conversationAuthorizationReady;
  const controls = apiClient.mode === 'tool-browser'
    ? { ...conversationControlAccess(null), canRespond: browserOperations?.messages_write === true && !conversation?.archived_at }
    : ordinaryControls;
  const canEditTitle = conversationAuthorizationReady && (apiClient.mode === 'tool-browser'
    ? browserOperations?.title_edit === true : controls.canManageConversation);
  // Include every authorization-relevant field in the loader generation. The
  // loader hook advances its request epoch synchronously when this changes,
  // preventing a response started before demotion from restoring restricted
  // state after the demotion cleanup effect has run.
  const conversationAuthorizationEpoch = currentConversationAccess
    ? [
        currentConversationAccess.conversation_id,
        currentConversationAccess.actor_user_id,
        currentConversationAccess.visibility,
        currentConversationAccess.admin_bypass ? 'admin' : 'member',
        [...currentConversationAccess.capabilities].sort().join(','),
        JSON.stringify(currentConversationAccess.browser_operations ?? null)
      ].join(':')
    : 'no-conversation-access';
  const currentConversationRequestRef = useRef({
    conversationId,
    authorizationEpoch: conversationAuthorizationEpoch
  });
  currentConversationRequestRef.current = {
    conversationId,
    authorizationEpoch: conversationAuthorizationEpoch
  };
  const isCurrentConversationAuthorizationRequest = useCallback(
    (requestConversationId: string, requestAuthorizationEpoch: string) =>
      currentConversationRequestRef.current.conversationId === requestConversationId &&
      currentConversationRequestRef.current.authorizationEpoch === requestAuthorizationEpoch,
    []
  );
  const [conversationProjection, setConversationProjection] =
    useState<ConversationProjection | null>(null);
  const [conversationProjectionLoading, setConversationProjectionLoading] = useState(false);
  const [interactionModeBusy, setInteractionModeBusy] = useState(false);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [initialMessagesLoaded, setInitialMessagesLoaded] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [runs, setRuns] = useState<CodexRun[]>([]);
  const [runEvents, setRunEvents] = useState<RunEvent[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  // This is intentionally only an ID. A foreground reauthorization unmounts
  // the terminal (including its output and WebSocket); after a renewed grant
  // it may ask the newly mounted panel to obtain a fresh token for this ID.
  const [terminalSelectedSessionId, setTerminalSelectedSessionId] = useState<string | null>(null);
  const [terminalResumeSessionId, setTerminalResumeSessionId] = useState<string | null>(null);
  // An authorization fingerprint can be unchanged after backgrounding. Keep a
  // separate generation so only a session listing fetched *after* that
  // foreground reauthorization can trigger a terminal resume.
  const terminalAuthorizationGenerationRef = useRef(0);
  const [terminalAuthorizationGeneration, setTerminalAuthorizationGeneration] = useState(0);
  const [terminalSessionSnapshotGeneration, setTerminalSessionSnapshotGeneration] = useState<number | null>(null);
  const terminalAutoReconnectAllowedRef = useRef(false);
  const [queueReorderBusy, setQueueReorderBusy] = useState(false);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [toolApprovals, setToolApprovals] = useState<ToolApproval[]>([]);
  const [toolApprovalBusyKey, setToolApprovalBusyKey] = useState<string | null>(null);
  const [sandboxes, setSandboxes] = useState<MlSandbox[]>([]);
  const [sandboxRuns, setSandboxRuns] = useState<MlSandboxRun[]>([]);
  const [approvalDismissedKey, setApprovalDismissedKey] = useState<string | null>(null);
  const [approvalResponseDraft, setApprovalResponseDraft] = useState<{
    requestKey: string | null;
    value: string;
  }>({ requestKey: null, value: '' });
  const [runErrorDismissedId, setRunErrorDismissedId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [holdQueuedMessages, setHoldQueuedMessages] = useState(false);
  const [shellAutoExpandMessageId, setShellAutoExpandMessageId] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [editingBusy, setEditingBusy] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [editingTitleBusy, setEditingTitleBusy] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [gitlabInfo, setGitlabInfo] = useState<ConversationGitlabInfo | null>(null);
  const [gitlabBusy, setGitlabBusy] = useState(false);
  const [gitlabError, setGitlabError] = useState<string | null>(null);
  const [multiGitlabApps, setMultiGitlabApps] = useState<AppGitlabInfo[]>([]);
  const [autopilotMode, setAutopilotMode] = useState<AutopilotMode>('semi');
  const [autopilotBusy, setAutopilotBusy] = useState(false);
  const [autopilotMenuOpen, setAutopilotMenuOpen] = useState(false);
  const [models, setModels] = useState<ConversationModelOption[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [squashOnMerge, setSquashOnMerge] = useState(true);
  const navigate = useNavigate();
  const [toolTokenStatus, setToolTokenStatus] = useState<ToolTokenStatus | null>(null);
  const [toolTokenValue, setToolTokenValue] = useState<string | null>(null);
  const [toolTokenScopes, setToolTokenScopes] = useState<string[]>(DEFAULT_TOOL_SCOPES);
  const [toolTokenBusy, setToolTokenBusy] = useState(false);
  const [showToolAccess, setShowToolAccess] = useState(false);
  const [execGrants, setExecGrants] = useState<ExecGrant[]>([]);
  const [execGrantKey, setExecGrantKey] = useState<string | null>(null);
  const [execGrantBusy, setExecGrantBusy] = useState(false);
  const [execGrantName, setExecGrantName] = useState('Temporary command grant');
  const [execGrantDurationAmount, setExecGrantDurationAmount] = useState(EXEC_GRANT_DEFAULT_DURATION);
  const [execGrantDurationUnit, setExecGrantDurationUnit] = useState<ExecutionGrantDurationUnit>('minutes');
  const [execGrantPrivileged, setExecGrantPrivileged] = useState(false);
  const [execGrantExecutorRefs, setExecGrantExecutorRefs] = useState('');
  const [execGrantMaxUses, setExecGrantMaxUses] = useState('');
  const [showConversationSecrets, setShowConversationSecrets] = useState(false);
  const [conversationSecrets, setConversationSecrets] = useState<ConversationAppSecret[]>([]);
  const [conversationOpsSecrets, setConversationOpsSecrets] = useState<OpsSecret[]>([]);
  const [conversationSecretsBusy, setConversationSecretsBusy] = useState(false);
  const [conversationSecretsError, setConversationSecretsError] = useState<string | null>(null);
  const [conversationSecretDraftKey, setConversationSecretDraftKey] = useState('');
  const [conversationSecretDraftType, setConversationSecretDraftType] = useState<'env' | 'file'>(
    'env'
  );
  const [conversationSecretDraftValue, setConversationSecretDraftValue] = useState('');
  const [conversationSecretDraftDescription, setConversationSecretDraftDescription] = useState('');
  const [expandedRunnerSteps, setExpandedRunnerSteps] = useState<Record<string, boolean>>({});
  // FixBugDialog state
  const [fixBugOpen, setFixBugOpen] = useState(false);
  const [fixBugMessageId, setFixBugMessageId] = useState<string>('');
  const [fixBugSpanId, setFixBugSpanId] = useState<string>('');
  const [apps, setApps] = useState<AppItem[]>([]);
  const [, setStreamState] = useState<'idle' | 'connecting' | 'live' | 'disconnected'>(
    'idle'
  );
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [inputValue, setInputValue] = useState(() => apiClient.mode === 'tool-browser' ? '' : loadDraft(conversationId));
  const [inputConversationId, setInputConversationId] = useState(conversationId);
  const [serverDraftLoadedConversationId, setServerDraftLoadedConversationId] = useState<
    string | null
  >(null);
  const [draftVersions, setDraftVersions] = useState<ConversationInputDraftVersion[]>([]);
  const [activeDraftVersionId, setActiveDraftVersionId] = useState<string | null>(null);
  const [draftHistoryOpen, setDraftHistoryOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<ConversationAttachment[]>([]);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [attachmentDeleteTarget, setAttachmentDeleteTarget] =
    useState<ConversationAttachment | null>(null);
  const conversationAccessRequestVersionRef = useRef(0);
  const foregroundAccessRevalidationGateRef = useRef(createForegroundRevalidationGate());

  useEffect(() => {
    const requestVersion = conversationAccessRequestVersionRef.current + 1;
    conversationAccessRequestVersionRef.current = requestVersion;
    const foregroundRevalidation = foregroundAccessRevalidationGateRef.current.pending;
    if (!conversationId) {
      terminalAutoReconnectAllowedRef.current = false;
      setConversationAccess(null);
      setConversationAccessLoading(false);
      setTerminalSessionSnapshotGeneration(null);
      setTerminalSelectedSessionId(null);
      setTerminalResumeSessionId(null);
      if (foregroundRevalidation) {
        foregroundAccessRevalidationGateRef.current.complete();
      }
      return;
    }
    let cancelled = false;
    setConversationAccess(null);
    setConversationAccessLoading(true);
    void apiClient
      .get<ConversationAccessSummary>(`/api/conversations/${conversationId}/access`)
      .then((access) => {
        if (!cancelled && conversationAccessRequestVersionRef.current === requestVersion) {
          if (!hasConversationCapability(access, CONVERSATION_CAPABILITIES.terminalAttach)) {
            terminalAutoReconnectAllowedRef.current = false;
            const nextTerminalAuthorizationGeneration = terminalAuthorizationGenerationRef.current + 1;
            terminalAuthorizationGenerationRef.current = nextTerminalAuthorizationGeneration;
            setTerminalAuthorizationGeneration(nextTerminalAuthorizationGeneration);
            setTerminalSessionSnapshotGeneration(null);
          }
          setConversationAccess(access);
        }
      })
      .catch(() => {
        // A 403 can race a membership revocation. Keep controls hidden rather
        // than retaining controls from the previously viewed conversation.
        if (!cancelled && conversationAccessRequestVersionRef.current === requestVersion) {
          terminalAutoReconnectAllowedRef.current = false;
          setConversationAccess(null);
          setTerminalSessionSnapshotGeneration(null);
          setTerminalSelectedSessionId(null);
          setTerminalResumeSessionId(null);
        }
      })
      .finally(() => {
        if (!cancelled && conversationAccessRequestVersionRef.current === requestVersion) {
          setConversationAccessLoading(false);
          if (foregroundRevalidation) {
            foregroundAccessRevalidationGateRef.current.complete();
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, conversationAccessRefreshVersion, conversationId]);

  useEffect(() => {
    let blurTimer: number | null = null;
    const markBackgrounded = () => {
      // Stop the terminal hook synchronously, before its own visibility
      // listener can request another WebSocket token. It will only be opened
      // again after access and a new session snapshot have both succeeded.
      terminalAutoReconnectAllowedRef.current = false;
      foregroundAccessRevalidationGateRef.current.markBackgrounded();
    };
    const markBlurred = () => {
      // Do not treat the short-lived focus hand-off that browsers perform
      // while activating an in-page control as a page-background event. A
      // genuine switch to another window remains unfocused on the next task.
      if (blurTimer !== null) {
        window.clearTimeout(blurTimer);
      }
      blurTimer = window.setTimeout(() => {
        blurTimer = null;
        if (!document.hasFocus()) {
          markBackgrounded();
        }
      }, 100);
    };
    const revalidateAfterForeground = () => {
      if (document.visibilityState !== 'visible') {
        markBackgrounded();
        return;
      }
      // Browsers commonly dispatch both visibilitychange and focus for the
      // same foreground transition. Ignore ordinary focus events within an
      // already visible page: Playwright and mobile browsers can emit those
      // while a control is being selected. Keep exactly one resource-scoped
      // refresh after an actual blur/hidden -> foreground transition, while
      // fail-closing controls before it completes.
      if (!foregroundAccessRevalidationGateRef.current.tryBeginAfterForeground()) {
        return;
      }
      // Invalidate a low-frequency poll before React schedules the refresh
      // effect, so its response cannot briefly restore old capabilities.
      conversationAccessRequestVersionRef.current += 1;
      terminalAutoReconnectAllowedRef.current = false;
      const nextTerminalAuthorizationGeneration = terminalAuthorizationGenerationRef.current + 1;
      terminalAuthorizationGenerationRef.current = nextTerminalAuthorizationGeneration;
      setTerminalAuthorizationGeneration(nextTerminalAuthorizationGeneration);
      setTerminalSessionSnapshotGeneration(null);
      // Do not accept a pre-background session request for this renewed
      // authorization generation.
      setSessions([]);
      setConversationAccess(null);
      setConversationAccessLoading(true);
      setConversationAccessRefreshVersion((version) => version + 1);
    };
    document.addEventListener('visibilitychange', revalidateAfterForeground);
    window.addEventListener('blur', markBlurred);
    window.addEventListener('focus', revalidateAfterForeground);
    // BFCache restores may use pageshow without a new focus event. The parent
    // owns that transition so child terminal hooks remain gated until this
    // access request and its new session snapshot have completed.
    window.addEventListener('pagehide', markBackgrounded, true);
    window.addEventListener('pageshow', revalidateAfterForeground, true);
    return () => {
      document.removeEventListener('visibilitychange', revalidateAfterForeground);
      window.removeEventListener('blur', markBlurred);
      window.removeEventListener('focus', revalidateAfterForeground);
      window.removeEventListener('pagehide', markBackgrounded, true);
      window.removeEventListener('pageshow', revalidateAfterForeground, true);
      if (blurTimer !== null) {
        window.clearTimeout(blurTimer);
      }
    };
  }, []);

  useEffect(() => {
    // A terminal attachment is never transferable to another conversation.
    terminalAutoReconnectAllowedRef.current = false;
    const nextTerminalAuthorizationGeneration = terminalAuthorizationGenerationRef.current + 1;
    terminalAuthorizationGenerationRef.current = nextTerminalAuthorizationGeneration;
    setTerminalAuthorizationGeneration(nextTerminalAuthorizationGeneration);
    setTerminalSessionSnapshotGeneration(null);
    setTerminalSelectedSessionId(null);
    setTerminalResumeSessionId(null);
  }, [conversationId]);

  const handleConversationAuthorizationLost = useCallback(() => {
    // Fail closed immediately, then let the access effect above confirm the
    // current membership. This prevents stale runtime controls after a live
    // revocation without treating an ordinary WebSocket disconnect as one.
    setConversationAccess(null);
    setConversationAccessLoading(true);
    setConversation(null);
    setConversationProjection(null);
    setConversationProjectionLoading(false);
    setMessages([]);
    setHasMoreMessages(false);
    setLoadingMessages(false);
    setInitialMessagesLoaded(false);
    setLoadingOlderMessages(false);
    setQueue([]);
    setRuns([]);
    setRunEvents([]);
    terminalAutoReconnectAllowedRef.current = false;
    setSessions([]);
    setTerminalSessionSnapshotGeneration(null);
    setTerminalSelectedSessionId(null);
    setTerminalResumeSessionId(null);
    setToolApprovals([]);
    setToolApprovalBusyKey(null);
    setSandboxes([]);
    setSandboxRuns([]);
    setApprovalDismissedKey(null);
    setApprovalResponseDraft({ requestKey: null, value: '' });
    setRunErrorDismissedId(null);
    setSending(false);
    setHoldQueuedMessages(false);
    setShellAutoExpandMessageId(null);
    setEditingMessageId(null);
    setEditingContent('');
    setEditingBusy(false);
    setEditingTitle(false);
    setEditingTitleValue('');
    setEditingTitleBusy(false);
    setGitlabInfo(null);
    setGitlabBusy(false);
    setGitlabError(null);
    setMultiGitlabApps([]);
    setAutopilotMode('semi');
    setAutopilotBusy(false);
    setAutopilotMenuOpen(false);
    setModels([]);
    setApps([]);
    setSelectedModelId(null);
    setModelMenuOpen(false);
    setToolTokenStatus(null);
    setToolTokenValue(null);
    setToolTokenScopes(DEFAULT_TOOL_SCOPES);
    setToolTokenBusy(false);
    setShowToolAccess(false);
    setExecGrants([]);
    setExecGrantKey(null);
    setExecGrantBusy(false);
    setShowConversationSecrets(false);
    setConversationSecrets([]);
    setConversationOpsSecrets([]);
    setConversationSecretsBusy(false);
    setConversationSecretsError(null);
    setConversationSecretDraftKey('');
    setConversationSecretDraftValue('');
    setConversationSecretDraftDescription('');
    setPendingAttachments([]);
    setAttachmentBusy(false);
    setAttachmentDeleteTarget(null);
    setAttachmentDeleteBusy(false);
    setFixBugOpen(false);
    setFixBugMessageId('');
    setFixBugSpanId('');
    setInputValue('');
    setInputConversationId('');
    setServerDraftLoadedConversationId(null);
    setDraftVersions([]);
    setActiveDraftVersionId(null);
    setDraftHistoryOpen(false);
    setExpandedRunnerSteps({});
    setShowJumpToLatest(false);
    setConversationAccessRefreshVersion((version) => version + 1);
  }, []);

  const delegatedGrantAuthorityLost = useCallback(async () => {
    handleConversationAuthorizationLost();
  }, [handleConversationAuthorizationLost]);

  // Membership changes do not necessarily close the conversation-updates
  // socket (for example contributor -> viewer). Re-read the server-derived
  // summary at a low frequency so capability-gated controls converge without
  // requiring a hard revocation or a page reload.
  useEffect(() => {
    if (!conversationId || !conversationAuthorizationReady) {
      return;
    }
    let cancelled = false;
    const refreshAccess = async () => {
      const requestVersion = conversationAccessRequestVersionRef.current + 1;
      conversationAccessRequestVersionRef.current = requestVersion;
      try {
        const access = await apiClient.get<ConversationAccessSummary>(
          `/api/conversations/${conversationId}/access`
        );
        if (
          !cancelled &&
          conversationAccessRequestVersionRef.current === requestVersion &&
          access.conversation_id === conversationId
        ) {
          if (!hasConversationCapability(access, CONVERSATION_CAPABILITIES.terminalAttach)) {
            terminalAutoReconnectAllowedRef.current = false;
            const nextTerminalAuthorizationGeneration = terminalAuthorizationGenerationRef.current + 1;
            terminalAuthorizationGenerationRef.current = nextTerminalAuthorizationGeneration;
            setTerminalAuthorizationGeneration(nextTerminalAuthorizationGeneration);
            setTerminalSessionSnapshotGeneration(null);
          }
          setConversationAccess(access);
        }
      } catch (err) {
        if (
          !cancelled &&
          conversationAccessRequestVersionRef.current === requestVersion &&
          isConversationAuthorizationFailure(err)
        ) {
          handleConversationAuthorizationLost();
        }
      }
    };
    const timer = window.setInterval(() => void refreshAccess(), CONVERSATION_ACCESS_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [apiClient, conversationAuthorizationReady, conversationId, handleConversationAuthorizationLost]);

  useEffect(() => {
    if (
      !conversationId ||
      conversation?.interaction_mode !== 'interactive_agent' ||
      !controls.canRunWorkspace
    ) {
      setConversationProjection(null);
      setConversationProjectionLoading(false);
      return;
    }
    let canceled = false;
    const loadProjection = async () => {
      setConversationProjectionLoading(true);
      try {
        const response = await apiClient.get<ConversationProjection>(
          `/api/conversations/${conversationId}/projection?limit=200`
        );
        if (!canceled) setConversationProjection(response);
      } catch (err) {
        if (!canceled) {
          reportErrorLocal(
            'Failed to load interactive agent activity',
            err,
            setError,
            setConversationError
          );
        }
      } finally {
        if (!canceled) setConversationProjectionLoading(false);
      }
    };
    void loadProjection();
    const timer = window.setInterval(() => void loadProjection(), 5000);
    return () => {
      canceled = true;
      window.clearInterval(timer);
    };
  }, [apiClient, conversation?.interaction_mode, conversationId, controls.canRunWorkspace, setError]);
  const [attachmentDeleteBusy, setAttachmentDeleteBusy] = useState(false);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const terminalPanelRef = useRef<HTMLDivElement | null>(null);
  const threadNearBottomRef = useRef(true);
  const initialScrollDoneRef = useRef(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const inputValueRef = useRef(inputValue);
  const autopilotMenuRef = useRef<HTMLDivElement | null>(null);
  const modelMenuRef = useRef<HTMLDivElement | null>(null);
  const draftHistoryMenuRef = useRef<HTMLDivElement | null>(null);
  const toolApprovalsInFlightRef = useRef(false);
  const sandboxesInFlightRef = useRef(false);
  const queueSpanByMessageId = useMemo(
    () =>
      queue.reduce<Record<string, string>>((acc, item) => {
        if (item.id) {
          acc[item.message_id] = item.id;
        }
        return acc;
      }, {}),
    [queue]
  );
  const formatLogPayload = (event: RunEvent) => normalizeUpdatePayload(event.payload);

  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  const applyServerDraftResponse = React.useCallback((draft: ConversationInputDraft) => {
    setDraftVersions(draft.versions ?? []);
    setActiveDraftVersionId(draft.version_id ?? null);
  }, []);

  useEffect(() => {
    if (!conversationId || !controls.canRespond) {
      setInputValue('');
      setInputConversationId('');
      setServerDraftLoadedConversationId(null);
      setDraftVersions([]);
      setActiveDraftVersionId(null);
      setDraftHistoryOpen(false);
      return;
    }

    if (apiClient.mode === 'tool-browser') {
      setInputValue('');
      setInputConversationId(conversationId);
      setDraftVersions([]);
      setActiveDraftVersionId(null);
      setDraftHistoryOpen(false);
      return;
    }

    const localDraft = loadDraft(conversationId);
    setInputValue(localDraft);
    setInputConversationId(conversationId);
    inputValueRef.current = localDraft;
    setServerDraftLoadedConversationId(null);
    setDraftVersions([]);
    setActiveDraftVersionId(null);
    setDraftHistoryOpen(false);

    let cancelled = false;
    void loadServerDraft(apiClient, conversationId)
      .then((draft) => {
        if (cancelled) {
          return;
        }
        const serverDraft = draft.content ?? '';
        applyServerDraftResponse(draft);
        if (inputValueRef.current === localDraft && !localDraft && serverDraft) {
          setInputValue(serverDraft);
          setInputConversationId(conversationId);
          inputValueRef.current = serverDraft;
        }
        setServerDraftLoadedConversationId(conversationId);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Failed to load conversation draft', err);
          setServerDraftLoadedConversationId(conversationId);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiClient, applyServerDraftResponse, conversationId, controls.canRespond]);

  useEffect(() => {
    if (apiClient.mode !== 'tool-browser' && conversationId && inputConversationId === conversationId) {
      saveDraft(conversationId, inputValue);
    }
  }, [apiClient.mode, conversationId, inputConversationId, inputValue]);

  useEffect(() => {
    if (
      apiClient.mode === 'tool-browser' ||
      !conversationId ||
      !controls.canRespond ||
      inputConversationId !== conversationId ||
      serverDraftLoadedConversationId !== conversationId
    ) {
      return;
    }
    if (!inputValue) {
      return;
    }
    const timer = window.setTimeout(() => {
      void saveServerDraft(apiClient, conversationId, inputValue, activeDraftVersionId)
        .then(applyServerDraftResponse)
        .catch((err) => {
          console.warn('Failed to save conversation draft', err);
        });
    }, DRAFT_SERVER_SAVE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    activeDraftVersionId,
    apiClient,
    applyServerDraftResponse,
    conversationId,
    controls.canRespond,
    inputConversationId,
    inputValue,
    serverDraftLoadedConversationId
  ]);

  useEffect(() => {
    if (!conversationId || !controls.canManageConversation) {
      setToolApprovals([]);
      return;
    }
    let cancelled = false;
    let timer: number | null = null;
    const schedule = (delayMs: number) => {
      if (!cancelled) {
        timer = window.setTimeout(() => {
          void loadToolApprovals();
        }, delayMs);
      }
    };
    const loadToolApprovals = async () => {
      if (toolApprovalsInFlightRef.current) {
        schedule(TOOL_APPROVAL_POLL_ACTIVE_MS);
        return;
      }
      if (!pageIsVisible()) {
        schedule(TOOL_APPROVAL_POLL_HIDDEN_MS);
        return;
      }
      toolApprovalsInFlightRef.current = true;
      let nextDelay = TOOL_APPROVAL_POLL_IDLE_MS;
      try {
        const data = await apiClient.get<ToolApproval[]>(
          `/api/conversations/${conversationId}/tool-approvals?status=pending&limit=10`
        );
        nextDelay = data.length > 0 ? TOOL_APPROVAL_POLL_ACTIVE_MS : TOOL_APPROVAL_POLL_IDLE_MS;
        if (!cancelled) {
          setToolApprovals(data);
        }
      } catch {
        if (!cancelled) {
          setToolApprovals([]);
        }
      } finally {
        toolApprovalsInFlightRef.current = false;
        schedule(nextDelay);
      }
    };
    void loadToolApprovals();
    return () => {
      cancelled = true;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [apiClient, controls.canManageConversation, conversationId]);

  const loadSessions = React.useCallback(async () => {
    if (!conversationId || !controls.canAttachTerminal) {
      setSessions([]);
      return;
    }
    const requestConversationId = conversationId;
    const requestAuthorizationEpoch = conversationAuthorizationEpoch;
    const requestTerminalAuthorizationGeneration = terminalAuthorizationGenerationRef.current;
    try {
      const data = await apiClient.get<Session[]>(
        `/api/conversations/${requestConversationId}/sessions`
      );
      if (
        !isCurrentConversationAuthorizationRequest(
          requestConversationId,
          requestAuthorizationEpoch
        ) || terminalAuthorizationGenerationRef.current !== requestTerminalAuthorizationGeneration
      ) {
        return;
      }
      setSessions(data);
      setTerminalSessionSnapshotGeneration(requestTerminalAuthorizationGeneration);
      terminalAutoReconnectAllowedRef.current = true;
    } catch (err) {
      if (
        !isCurrentConversationAuthorizationRequest(
          requestConversationId,
          requestAuthorizationEpoch
        )
      ) {
        return;
      }
      reportErrorLocal('Failed to load sessions', err, setError, setConversationError);
      terminalAutoReconnectAllowedRef.current = false;
      setTerminalSessionSnapshotGeneration(null);
      setSessions([]);
    }
  }, [
    apiClient,
    conversationAuthorizationEpoch,
    conversationId,
    controls.canAttachTerminal,
    isCurrentConversationAuthorizationRequest,
    setError
  ]);

  useEffect(() => {
    if (!controls.canAttachTerminal) {
      setSessions([]);
      return;
    }
    void loadSessions();
  }, [controls.canAttachTerminal, loadSessions]);

  // Poll sandbox status for this conversation
  useEffect(() => {
    if (!conversationId || !controls.canRunWorkspace) {
      setSandboxes([]);
      setSandboxRuns([]);
      return;
    }
    let cancelled = false;
    let timer: number | null = null;
    const schedule = (delayMs: number) => {
      if (!cancelled) {
        timer = window.setTimeout(() => {
          void loadSandboxes();
        }, delayMs);
      }
    };
    const loadSandboxes = async () => {
      if (sandboxesInFlightRef.current) {
        schedule(SANDBOX_POLL_ACTIVE_MS);
        return;
      }
      if (!pageIsVisible()) {
        schedule(SANDBOX_POLL_HIDDEN_MS);
        return;
      }
      sandboxesInFlightRef.current = true;
      let nextDelay = SANDBOX_POLL_IDLE_MS;
      try {
        const [sandboxData, runData] = await Promise.all([
          apiClient.get<MlSandbox[]>(
            `/api/conversations/${conversationId}/ml-sandboxes?limit=20`
          ),
          apiClient.get<MlSandboxRun[]>(
            `/api/conversations/${conversationId}/ml-sandbox-runs?limit=20`
          ),
        ]);
        const hasActiveSandbox =
          sandboxData.some((sandbox) => sandboxStatusIsActive(sandbox.build_status)) ||
          runData.some((run) => sandboxRunStatusIsActive(run.status));
        nextDelay = hasActiveSandbox ? SANDBOX_POLL_ACTIVE_MS : SANDBOX_POLL_IDLE_MS;
        if (!cancelled) {
          setSandboxes(sandboxData);
          setSandboxRuns(runData);
        }
      } catch {
        if (!cancelled) {
          setSandboxes([]);
          setSandboxRuns([]);
        }
      } finally {
        sandboxesInFlightRef.current = false;
        schedule(nextDelay);
      }
    };
    void loadSandboxes();
    return () => {
      cancelled = true;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [apiClient, controls.canRunWorkspace, conversationId]);

  async function respondToToolApproval(
    approvalId: string,
    payload: { decision: 'approve' | 'deny'; remember?: boolean }
  ) {
    const busyKey = `${approvalId}:${payload.decision}:${payload.remember ? 'remember' : 'once'}`;
    if (toolApprovalBusyKey === busyKey) {
      return;
    }
    setToolApprovalBusyKey(busyKey);
    try {
      await apiClient.post(
        `/api/conversations/${conversationId}/tool-approvals/${approvalId}/respond`,
        payload
      );
      const refreshed = await apiClient.get<ToolApproval[]>(
        `/api/conversations/${conversationId}/tool-approvals?status=pending&limit=10`
      );
      setToolApprovals(refreshed);
    } catch (err) {
      reportErrorLocal(
        'Failed to respond to tool approval',
        err,
        setError,
        setConversationError
      );
    } finally {
      setToolApprovalBusyKey((current) => (current === busyKey ? null : current));
    }
  }
  const {
    eventsError,
    setEventsError,
    showRunnerSidebar,
    setShowRunnerSidebar,
    runnerSidebarWidth,
    isMobileLayout,
    selectedActivityMessageId,
    setSelectedActivityMessageId,
    queueActivityByMessageId,
    queueActivityLoadingByMessageId,
    activityTreeByMessageId,
    activityTreeLoadingByMessageId,
    expandedActivityStages,
    expandedActivityCommands,
    visibleActivityStagesByMessage,
    visibleActivityCommandsByStage,
    visibleActivityOutputLinesByCommand,
    loadingOlderActivityByMessageId,
    loadingOlderCommandOutput,
    copyingRawCommandOutput,
    conversationBodyRef,
    conversationBodyClassName,
    conversationBodyStyle,
    startRunnerSidebarResize,
    onRunnerSidebarResize,
    finishRunnerSidebarResize,
    handleRunnerSidebarResizeKeyDown,
    toggleExpandedActivityStage,
    toggleExpandedActivityCommand,
    showMoreActivityStages,
    showMoreActivityCommands,
    showMoreLoadedActivityOutput,
    openMessageActivitySidebar,
    loadOlderActivityCommands,
    loadOlderCommandOutput,
    copyRawAgentCommandOutput,
    ensureRunnerSidebarVisible,
    closeRunnerSidebar,
    loadQueueActivity,
    loadRunActivityTree,
    messageVisibleStageCountKey,
    stageExpandKey,
    stageVisibleCommandCountKey,
    commandExpandKey,
    commandVisibleOutputLineCountKey
  } = useConversationActivitySidebar({
    apiClient,
    conversationId,
    canLoadRuntime: controls.canRunWorkspace,
    shellAutoExpandMessageId,
    messages,
    setShellAutoExpandMessageId,
    setError,
    reportErrorLocal,
    runnerSidebarWidthStorageKey: RUNNER_SIDEBAR_WIDTH_STORAGE_KEY,
    mobileLayoutQuery: MOBILE_LAYOUT_QUERY,
    runnerSidebarMinWidth: RUNNER_SIDEBAR_MIN_WIDTH,
    runnerThreadMinWidth: RUNNER_THREAD_MIN_WIDTH,
    runnerSidebarDefaultWidthRatio: RUNNER_SIDEBAR_DEFAULT_WIDTH_RATIO,
    runnerSidebarDefaultWidthFallback: RUNNER_SIDEBAR_DEFAULT_WIDTH_FALLBACK,
    activityStagesPage: ACTIVITY_STAGES_PAGE,
    activityStageCommandsPage: ACTIVITY_STAGE_COMMANDS_PAGE,
    activityOutputLinesPage: ACTIVITY_OUTPUT_LINES_PAGE
  });
  const {
    activeRun,
    runByMessageId,
    streamingRun,
    hasInteractiveApproval,
    hasActiveRun,
    queueByMessageId,
    autopilotMessageIds,
    orderedQueue,
    queueOrderByMessageId,
    orderedQueuedMessageIds,
    runnerState,
    assistantText,
    assistantByUserId,
    assignedAssistantIds,
    displayMessages,
    messagePreviewById,
    latestUserMessage,
    latestTurnStatus,
    effectiveConversationStatus,
    composerWillQueue,
    activeOutputEvents,
    activeUpdateEvents,
    activeRunActivityTree,
    effectiveSelectedActivityMessageId,
    selectedActivityRun,
    selectedActivityQueueItem,
    selectedQueueActivityEvents,
    selectedPreparationActivityGrouped,
    selectedQueueActivityGrouped,
    selectedActivityTree,
    selectedActivityCommandCount,
    selectedActivityLoadingOlder,
    selectedActivityRunId,
    selectedActivityQueueLoading,
    activeLivePreview
  } = useConversationDerivedState({
    messages,
    queue,
    runs,
    runEvents,
    selectedActivityMessageId,
    queueActivityByMessageId,
    loadingOlderActivityByMessageId,
    queueActivityLoadingByMessageId,
    activityTreeByMessageId,
    holdQueuedMessages,
    conversationStatus:
      conversation?.status === 'queued' ||
      conversation?.status === 'processing' ||
      conversation?.status === 'idle'
        ? conversation.status
        : null,
    formatLogPayload,
    runnerPhaseProgress: RUNNER_PHASE_PROGRESS
  });
  const activeRunErrorRenderedInThread = Boolean(
    activeRun?.status === 'error' &&
      activeRun.message_id &&
      displayMessages.some(
        (message) => message.role === 'user' && message.id === activeRun.message_id
      )
  );
  const conversationSecretAppId = conversation?.app_id ?? appId;
  const {
    oldestMessageIdRef,
    runCursorRef,
    currentConversationIdRef,
    conversationStateVersionRef,
    liveStateRevisionRef,
    captureConversationStateVersion,
    isCurrentConversationRequest,
    loadConversation,
    loadConversationAutopilot,
    loadModels,
    loadGitlabInfo,
    loadMultiGitlabInfo,
    loadMessages,
    loadOlderMessages,
    loadQueue,
    loadRuns,
    loadToolTokenStatus,
    loadConversationSecrets,
    loadAll,
    resetConversationDerivedState
  } = useConversationPageLoaders({
    apiClient,
    appId,
    conversationId,
    conversation,
    models,
    loadingMessages,
    loadingOlderMessages,
    conversationSecretAppId,
    authorizationEpoch: conversationAuthorizationEpoch,
    canLoadToolTokenStatus: controls.canManageTools,
    canLoadGitlab: controls.canReadCode,
    canLoadAutopilot: controls.canManageConversation,
    canLoadModels: controls.canRespond,
    canLoadRuntime: controls.canRunWorkspace,
    setError,
    reportErrorLocal,
    setConversation,
    setMessages,
    setHasMoreMessages,
    setLoadingMessages,
    setInitialMessagesLoaded,
    setLoadingOlderMessages,
    setQueue,
    setRuns,
    setRunEvents,
    setConversationError,
    setGitlabInfo,
    setMultiGitlabApps,
    setGitlabError,
    setAutopilotMode,
    setModels,
    setApps,
    setToolTokenStatus,
    setToolTokenValue,
    setToolTokenScopes,
    setConversationSecrets,
    setConversationOpsSecrets,
    setConversationSecretsBusy,
    setConversationSecretsError,
    setConversationSecretDraftKey
  });
  const isArchived = Boolean(conversation?.archived_at);
  const scopedRefreshRef = useRef(loadMessages);
  scopedRefreshRef.current = loadMessages;
  useEffect(() => {
    if (apiClient.mode !== 'tool-browser' || !conversationAuthorizationReady) return;
    // Original thread polling is explicitly not stream/terminal parity.
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void scopedRefreshRef.current(true);
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [apiClient.mode, conversationAuthorizationReady, conversationId]);
  const pipelineStatusClass = (status?: string | null) => {
    if (!status) {
      return 'badge';
    }
    const normalized = status.toLowerCase();
    if (['failed', 'canceled', 'skipped'].includes(normalized)) {
      return 'badge badge--error';
    }
    if (['success'].includes(normalized)) {
      return 'badge badge--success';
    }
    if (['running', 'pending', 'created', 'manual'].includes(normalized)) {
      return 'badge badge--warning';
    }
    return 'badge';
  };
  const messageStatusBadgeClass = (status: MessageStatus, messageQueue?: QueueItem | null) => {
    if (status === 'error') {
      return 'badge badge--error';
    }
    if (status === 'done') {
      return 'badge badge--success';
    }
    if (status === 'processing') {
      return 'badge badge--processing';
    }
    if (status === 'queued') {
      return messageQueue?.manual_hold ? 'badge badge--queued-held' : 'badge badge--queued';
    }
    return 'badge';
  };
  const messageStatusLabel = (status: MessageStatus, messageQueue?: QueueItem | null) => {
    if (status !== 'queued') {
      return status;
    }
    return messageQueue?.manual_hold ? 'queued (paused)' : 'queued (waiting)';
  };
  const mrStatusClass = (status?: string | null) => {
    if (!status) {
      return 'badge';
    }
    const normalized = status.toLowerCase();
    if (['merged'].includes(normalized)) {
      return 'badge badge--success';
    }
    if (['closed'].includes(normalized)) {
      return 'badge badge--error';
    }
    if (['opened', 'open', 'locked'].includes(normalized)) {
      return 'badge badge--warning';
    }
    return 'badge';
  };

  useDismissibleMenu(autopilotMenuOpen, autopilotMenuRef, () => setAutopilotMenuOpen(false));
  useDismissibleMenu(modelMenuOpen, modelMenuRef, () => setModelMenuOpen(false));
  useDismissibleMenu(draftHistoryOpen, draftHistoryMenuRef, () => setDraftHistoryOpen(false));

  // The save handler fences late responses with the loader's existing request
  // generation. Clear the form on its invalidation inputs even when title
  // permission survives; an old finally must not clear a newer save's busy flag.
  useEffect(() => {
    setEditingTitle(false);
    setEditingTitleValue('');
    setEditingTitleBusy(false);
  }, [conversationId, conversationAuthorizationEpoch, conversationAuthorizationReady]);

  useEffect(() => {
    if (!canEditTitle) {
      setEditingTitle(false);
      setEditingTitleValue('');
      setEditingTitleBusy(false);
    }
    if (!controls.canManageConversation) {
      setEditingMessageId(null);
      setAutopilotMenuOpen(false);
      setFixBugOpen(false);
    }
    if (!controls.canManageSecrets) {
      setShowConversationSecrets(false);
    }
    if (!controls.canManageTools) {
      setShowToolAccess(false);
    }
  }, [
    canEditTitle,
    controls.canManageConversation,
    controls.canManageSecrets,
    controls.canManageTools
  ]);

  // A demotion can retain conversation:read while removing a narrower
  // workspace/tool capability. Clear data that was loaded under the old
  // capability rather than leaving a viewer with a stale runtime panel until
  // the next route change.
  useEffect(() => {
    if (!controls.canRunWorkspace) {
      setConversationProjection(null);
      setConversationProjectionLoading(false);
      setQueue([]);
      setRuns([]);
      setRunEvents([]);
      setSandboxes([]);
      setSandboxRuns([]);
      setShowRunnerSidebar(false);
      setShellAutoExpandMessageId(null);
    }
    if (!controls.canAttachTerminal) {
      setSessions([]);
      // During a foreground refresh access is deliberately unknown, so retain
      // only the opaque resume ID until the new summary arrives. Once it has
      // conclusively removed terminal:attach, discard that ID as well.
      if (!conversationAccessLoading) {
        terminalAutoReconnectAllowedRef.current = false;
        setTerminalSessionSnapshotGeneration(null);
        setTerminalSelectedSessionId(null);
        setTerminalResumeSessionId(null);
      }
    }
    if (!controls.canReadCode) {
      setGitlabInfo(null);
      setGitlabError(null);
      setMultiGitlabApps([]);
    }
    if (!controls.canRespond) {
      setModels([]);
      setApps([]);
      setSelectedModelId(null);
      setModelMenuOpen(false);
    }
    if (!controls.canManageConversation) {
      setToolApprovals([]);
    }
    if (!controls.canManageTools) {
      setToolTokenStatus(null);
      setToolTokenValue(null);
      setToolTokenScopes(DEFAULT_TOOL_SCOPES);
      setExecGrants([]);
      setExecGrantKey(null);
    }
    if (!controls.canManageSecrets) {
      setConversationSecrets([]);
      setConversationOpsSecrets([]);
      setConversationSecretsError(null);
    }
    if (!controls.canPublishArtifacts) {
      setPendingAttachments([]);
      setAttachmentDeleteTarget(null);
    }
  }, [
    controls.canAttachTerminal,
    controls.canManageConversation,
    controls.canManageSecrets,
    controls.canManageTools,
    controls.canPublishArtifacts,
    controls.canReadCode,
    controls.canRespond,
    controls.canRunWorkspace,
    conversationAccessLoading,
    setShowRunnerSidebar
  ]);

  const selectModel = (modelId: string) => {
    if (!models.some(model => model.id === modelId)) return;
    setSelectedModelId(modelId);
    if (apiClient.mode === 'tool-browser') return;
    if (typeof window !== 'undefined') window.localStorage.setItem(LAST_SELECTED_MODEL_STORAGE_KEY, modelId);
  };

  const handleSelectDraftVersion = React.useCallback(
    (version: ConversationInputDraftVersion) => {
      if (!conversationId || apiClient.mode === 'tool-browser') {
        return;
      }
      setInputConversationId(conversationId);
      setInputValue(version.content);
      inputValueRef.current = version.content;
      setActiveDraftVersionId(version.id);
      void saveServerDraft(apiClient, conversationId, version.content, version.id)
        .then(applyServerDraftResponse)
        .catch((err) => {
          console.warn('Failed to restore conversation draft version', err);
        });
    },
    [apiClient, applyServerDraftResponse, conversationId]
  );

  const handleMessageSent = React.useCallback(
    (sentConversationId: string) => {
      if (apiClient.mode === 'tool-browser') return;
      setActiveDraftVersionId(null);
      void clearServerDraft(apiClient, sentConversationId)
        .then(applyServerDraftResponse)
        .catch((err) => {
          console.warn('Failed to clear conversation draft', err);
        });
    },
    [apiClient, applyServerDraftResponse]
  );

  const handleComposerInputChange = React.useCallback(
    (value: string) => {
      setInputConversationId(conversationId);
      setInputValue(value);
    },
    [conversationId]
  );

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault(); event.currentTarget.form?.requestSubmit();
    }
  }

  function handleEditKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Escape') {
      event.preventDefault(); cancelEditing();
      return;
    }
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      if (editingMessageId) {
        void saveEditedMessage(editingMessageId);
      }
    }
  }

  function startEditingTitle() {
    if (!canEditTitle) return;
    setEditingTitleValue(conversation?.title ?? ''); setEditingTitle(true);
  }

  function cancelEditingTitle() {
    setEditingTitle(false); setEditingTitleValue('');
  }

  function toggleToolScope(scope: string) {
    setToolTokenScopes((prev) => {
      if (prev.includes(scope)) {
        return prev.filter((value) => value !== scope);
      }
      return [...prev, scope];
    });
  }

  useConversationPageEffects({
    apiClient,
    appId,
    conversationId,
    conversationTitle: conversation?.title,
    conversationAppId: conversation?.app_id,
    authorizationReady: conversationAuthorizationReady,
    canLoadRuntime: controls.canRunWorkspace,
    onConversationAuthorizationLost: handleConversationAuthorizationLost,
    showConversationSecrets,
    streamingRun,
    selectedActivityMessageId,
    showRunnerSidebar,
    activityTreeByMessageId,
    activityTreeLoadingByMessageId,
    runByMessageId,
    messages,
    models,
    selectedModelId,
    assistantText,
    activeOutputEventCount: activeOutputEvents.length,
    activeUpdateEventCount: activeUpdateEvents.length,
    hasMoreMessages,
    loadingOlderMessages,
    resetConversationDerivedState,
    loadAll,
    loadConversationSecrets,
    loadRunActivityTree,
    loadOlderMessages,
    runCursorRef,
    liveStateRevisionRef,
    threadRef,
    threadNearBottomRef,
    initialScrollDoneRef,
    setError,
    setMessages,
    setQueue,
    setRuns,
    setRunEvents,
    setEventsError,
    setQueueReorderBusy,
    setShowConversationSecrets,
    setConversationSecretDraftType,
    setConversationSecretDraftDescription,
    setSelectedModelId,
    setShellAutoExpandMessageId,
    setStreamState,
    setShowJumpToLatest,
    setAutopilotMenuOpen,
    setExpandedRunnerSteps,
    setApprovalDismissedKey,
    setRunErrorDismissedId,
    setSelectedActivityMessageId
  });

  const approvalRequest = useMemo<ApprovalRequest | null>(() => {
    if (!hasInteractiveApproval) {
      return null;
    }
    for (let i = runEvents.length - 1; i >= 0; i -= 1) {
      const event = runEvents[i];
      if (event.stream === 'assistant') {
        continue;
      }
      const request = parseApprovalRequestFromPayload(event.payload);
      if (!request) {
        continue;
      }
      const key = request.id ?? request.raw ?? null;
      if (!approvalDismissedKey || key !== approvalDismissedKey) {
        return request;
      }
    }
    return null;
  }, [runEvents, approvalDismissedKey, hasInteractiveApproval]);
  const approvalRequestKey = approvalRequest?.id ?? approvalRequest?.raw ?? null;
  const approvalResponseValue =
    approvalResponseDraft.requestKey === approvalRequestKey ? approvalResponseDraft.value : '';

  const {
    toggleArchive,
    sendMessage,
    updateAutopilotMode,
    startEditing,
    cancelEditing,
    saveEditedMessage,
    saveEditedTitle,
    createToolToken,
    revokeToolToken,
    openToolAccess,
    copyToolToken,
    copyMessageContent,
    cancelQueue,
    allowPipelineWait,
    stopPipelineWait,
    startQueuedMessage,
    toggleQueuedMessageHold,
    moveQueuedMessage,
    submitApproval,
    createMergeRequest,
    mergeMergeRequest,
    saveConversationSecretOverride,
    resetConversationSecretOverride
  } = useConversationPageActions({
    apiClient,
    navigate,
    conversationId,
    isArchived,
    holdQueuedMessages,
    selectedModelId,
    orderedQueuedMessageIds,
    orderedQueue,
    queueReorderBusy,
    approvalBusy,
    editingBusy,
    editingTitleBusy,
    canEditTitle,
    toolTokenBusy,
    canManageTools: controls.canManageTools,
    gitlabBusy,
    autopilotBusy,
    autopilotMode,
    conversation,
    messages,
    pendingAttachments,
    inputValue,
    editingContent,
    editingMessageId,
    editingTitleValue,
    toolTokenValue,
    toolTokenStatusActiveScopes: toolTokenStatus?.active ? toolTokenStatus.scopes : null,
    toolTokenScopes,
    conversationSecretAppId,
    conversationOpsSecrets,
    conversationSecretDraftKey,
    conversationSecretDraftType,
    conversationSecretDraftValue,
    conversationSecretDraftDescription,
    approvalRequest,
    approvalResponseValue,
    streamingRunId: streamingRun?.id ?? null,
    streamingRunApprovalFifo: streamingRun?.approval_fifo ?? null,
    onMessageSent: handleMessageSent,
    setError,
    reportErrorLocal,
    captureConversationStateVersion,
    isCurrentConversationRequest,
    loadConversation,
    loadConversationAutopilot,
    loadQueue,
    loadRuns,
    loadToolTokenStatus,
    loadConversationSecrets,
    loadGitlabInfo,
    loadMessages,
    setConversation,
    setMessages,
    setQueue,
    setInputValue,
    setPendingAttachments,
    setSending,
    setApprovalBusy,
    setApprovalDismissedKey,
    setEditingMessageId,
    setEditingContent,
    setEditingBusy,
    setEditingTitle,
    setEditingTitleValue,
    setEditingTitleBusy,
    setConversationError,
    setGitlabBusy,
    setAutopilotBusy,
    setAutopilotMenuOpen,
    setToolTokenBusy,
    setToolTokenValue,
    setToolTokenStatus,
    setToolTokenScopes,
    setShowToolAccess,
    setConversationSecretsBusy,
    setConversationSecretsError,
    setConversationSecretDraftValue,
    setQueueReorderBusy,
    setShellAutoExpandMessageId
  });

  const showRunnerBanner = Boolean(hasActiveRun && runnerState.message);
  const runnerProgressLabel =
    runnerState.progress !== null ? Math.round(runnerState.progress) : null;
  const selectedActivityLoading = effectiveSelectedActivityMessageId
    ? Boolean(activityTreeLoadingByMessageId[effectiveSelectedActivityMessageId])
    : false;

  const hasPromptOptions = Boolean(approvalRequest?.options && approvalRequest.options.length > 0);
  const allowFreeTextResponse = Boolean(approvalRequest && (approvalRequest.allow_free_text || (approvalRequest.kind === 'input' && !hasPromptOptions)));
  const conversationSecretOverridesByKey = useMemo(
    () => new Map(conversationSecrets.map((secret) => [secret.key, secret])),
    [conversationSecrets]
  );
  const selectedConversationOpsSecret = conversationOpsSecrets.find(
    (secret) => secret.key === conversationSecretDraftKey
  ) ?? null;

  const loadExecGrants = useCallback(async () => {
    if (!conversationId || !controls.canManageTools) {
      setExecGrants([]);
      return;
    }
    const requestAuthorizationEpoch = conversationAuthorizationEpoch;
    try {
      const data = await apiClient.get<ExecGrantListResponse>(
        `/api/conversations/${conversationId}/exec-grants`
      );
      if (
        !isCurrentConversationAuthorizationRequest(conversationId, requestAuthorizationEpoch)
      ) {
        return;
      }
      setExecGrants(data.grants ?? []);
    } catch (err) {
      if (
        !isCurrentConversationAuthorizationRequest(conversationId, requestAuthorizationEpoch)
      ) {
        return;
      }
      reportErrorLocal('Failed to load execution grants', err, setError, setConversationError);
    }
  }, [
    apiClient,
    conversationAuthorizationEpoch,
    conversationId,
    controls.canManageTools,
    isCurrentConversationAuthorizationRequest,
    setError
  ]);

  useEffect(() => {
    setExecGrants([]);
    setExecGrantKey(null);
    setExecGrantBusy(false);
  }, [conversationId]);

  useEffect(() => {
    if (!showToolAccess || !conversationId || !controls.canManageTools) {
      return;
    }
    void loadExecGrants();
  }, [showToolAccess, conversationId, controls.canManageTools, loadExecGrants]);

  async function createExecGrant(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    if (!conversationId || execGrantBusy || !controls.canManageTools) {
      return;
    }
    const duration = parseExecutionGrantDuration(execGrantDurationAmount, execGrantDurationUnit);
    if (duration.minutes === null) {
      setConversationError(duration.error);
      return;
    }
    const requestConversationId = conversationId;
    const requestAuthorizationEpoch = conversationAuthorizationEpoch;
    setExecGrantBusy(true);
    setExecGrantKey(null);
    const executorRefs = execGrantExecutorRefs
      .split(/[\s,]+/)
      .map((value) => value.trim())
      .filter(Boolean);
    const maxUses = execGrantMaxUses.trim() ? Number(execGrantMaxUses.trim()) : null;
    try {
      const data = await apiClient.post<ExecGrantCreateResponse>(
        `/api/conversations/${requestConversationId}/exec-grants`,
        {
          name: execGrantName,
          ttl_minutes: duration.minutes,
          modes: execGrantPrivileged ? ['user', 'privileged'] : ['user'],
          executor_refs: executorRefs,
          max_uses: maxUses && Number.isFinite(maxUses) && maxUses > 0 ? maxUses : undefined
        }
      );
      if (
        !isCurrentConversationAuthorizationRequest(
          requestConversationId,
          requestAuthorizationEpoch
        )
      ) {
        return;
      }
      setExecGrantKey(data.key);
      setExecGrants((prev) => [data.grant, ...prev.filter((grant) => grant.id !== data.grant.id)]);
    } catch (err) {
      if (
        !isCurrentConversationAuthorizationRequest(
          requestConversationId,
          requestAuthorizationEpoch
        )
      ) {
        return;
      }
      reportErrorLocal('Failed to create execution grant', err, setError, setConversationError);
    } finally {
      if (
        isCurrentConversationAuthorizationRequest(
          requestConversationId,
          requestAuthorizationEpoch
        )
      ) {
        setExecGrantBusy(false);
      }
    }
  }

  async function extendExecGrant(grant: ExecGrant, minutes: number): Promise<boolean> {
    if (!conversationId || execGrantBusy || !controls.canManageTools) return false;
    const requestConversationId = conversationId;
    const requestEpoch = conversationAuthorizationEpoch;
    setExecGrantBusy(true);
    try {
      const updated = await apiClient.post<ExecGrant>(`/api/conversations/${requestConversationId}/exec-grants/${grant.id}/extend`, {
        ttl_minutes: minutes, expected_expires_at: grant.expires_at
      });
      if (!isCurrentConversationAuthorizationRequest(requestConversationId, requestEpoch)) return false;
      setExecGrants(previous => previous.map(row => row.id === updated.id ? updated : row));
      return true;
    } catch (err) {
      if (!isCurrentConversationAuthorizationRequest(requestConversationId, requestEpoch)) return false;
      await loadExecGrants();
      if (!isCurrentConversationAuthorizationRequest(requestConversationId, requestEpoch)) return false;
      reportErrorLocal('Failed to extend execution grant', err, setError, setConversationError);
      return false;
    } finally {
      if (isCurrentConversationAuthorizationRequest(requestConversationId, requestEpoch)) setExecGrantBusy(false);
    }
  }

  async function revokeExecGrant(grantId: string) {
    if (!conversationId || execGrantBusy) {
      return;
    }
    setExecGrantBusy(true);
    try {
      const updated = await apiClient.del<ExecGrant>(
        `/api/conversations/${conversationId}/exec-grants/${grantId}`
      );
      setExecGrants((prev) => prev.map((grant) => (grant.id === updated.id ? updated : grant)));
    } catch (err) {
      reportErrorLocal('Failed to revoke execution grant', err, setError, setConversationError);
    } finally {
      setExecGrantBusy(false);
    }
  }

  async function copyExecGrantKey() {
    if (!execGrantKey) {
      return;
    }
    try {
      await navigator.clipboard.writeText(execGrantKey);
    } catch (err) {
      reportErrorLocal('Failed to copy execution grant key', err, setError, setConversationError);
    }
  }

  async function uploadComposerFiles(files: FileList | null) {
    if (!files || files.length === 0 || !conversationId || !controls.canPublishArtifacts) {
      return;
    }
    const requestConversationId = conversationId;
    const requestAuthorizationEpoch = conversationAuthorizationEpoch;
    setAttachmentBusy(true);
    try {
      const uploaded: ConversationAttachment[] = [];
      for (const file of Array.from(files)) {
        const contentBase64 = await fileToBase64(file);
        if (
          !isCurrentConversationAuthorizationRequest(
            requestConversationId,
            requestAuthorizationEpoch
          )
        ) {
          return;
        }
        const attachment = await apiClient.post<ConversationAttachment>(
          `/api/conversations/${requestConversationId}/attachments`,
          {
            filename: file.name,
            content_type: file.type || undefined,
            content_base64: contentBase64
          }
        );
        if (
          !isCurrentConversationAuthorizationRequest(
            requestConversationId,
            requestAuthorizationEpoch
          )
        ) {
          return;
        }
        uploaded.push(attachment);
      }
      setPendingAttachments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      if (
        !isCurrentConversationAuthorizationRequest(
          requestConversationId,
          requestAuthorizationEpoch
        )
      ) {
        return;
      }
      reportErrorLocal('Failed to attach file', err, setError, setConversationError);
    } finally {
      if (
        isCurrentConversationAuthorizationRequest(
          requestConversationId,
          requestAuthorizationEpoch
        )
      ) {
        setAttachmentBusy(false);
      }
    }
  }

  function requestDeleteAttachment(attachment: ConversationAttachment) {
    setAttachmentDeleteTarget(attachment);
  }

  async function confirmDeleteAttachment() {
    const attachment = attachmentDeleteTarget;
    if (!attachment || attachmentDeleteBusy) {
      return;
    }
    setAttachmentDeleteBusy(true);
    try {
      await apiClient.del(`/api/conversations/${conversationId}/attachments/${attachment.id}`);
      setPendingAttachments((prev) => prev.filter((item) => item.id !== attachment.id));
      setMessages((prev) =>
        prev.map((message) => ({
          ...message,
          attachments: (message.attachments ?? []).filter((item) => item.id !== attachment.id)
        }))
      );
      setAttachmentDeleteTarget(null);
    } catch (err) {
      reportErrorLocal('Failed to delete attachment', err, setError, setConversationError);
    } finally {
      setAttachmentDeleteBusy(false);
    }
  }

  async function downloadAttachment(attachment: ConversationAttachment) {
    try {
      const blob = await apiClient.blob(
        `/api/conversations/${conversationId}/attachments/${attachment.id}/download`
      );
      downloadBlob(blob, attachment.filename);
    } catch (err) {
      reportErrorLocal('Failed to download attachment', err, setError, setConversationError);
    }
  }

  async function previewAttachment(attachment: ConversationAttachment) {
    try {
      const blob = await apiClient.blob(
        `/api/conversations/${conversationId}/attachments/${attachment.id}/download`
      );
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      reportErrorLocal('Failed to preview attachment', err, setError, setConversationError);
    }
  }

  function showTerminalPanel() {
    if (isMobileLayout && showRunnerSidebar) {
      closeRunnerSidebar();
    }
    window.setTimeout(() => {
      const terminalPanel = terminalPanelRef.current;
      if (!terminalPanel) {
        return;
      }
      terminalPanel.focus({ preventScroll: true });
      terminalPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  async function setInteractionMode(mode: 'chat' | 'interactive_agent') {
    if (!conversation || interactionModeBusy || conversation.interaction_mode === mode) return;
    setInteractionModeBusy(true);
    try {
      const updated = await apiClient.patch<Conversation>(
        `/api/conversations/${conversationId}`,
        { interaction_mode: mode }
      );
      setConversation(updated);
      setConversationError(null);
    } catch (err) {
      reportErrorLocal(
        'Failed to update conversation experience',
        err,
        setError,
        setConversationError
      );
    } finally {
      setInteractionModeBusy(false);
    }
  }

  // Do not leave the old header, thread, terminal, or cached workspace data
  // mounted while access is being refreshed or after the server denies it.
  if (authorizationView === 'loading') {
    return (
      <section className="grid conversation-page-lane51" aria-label="Conversation">
        <div className="card full conversation-shell conversation-page-lane51__access">
          <p className="conversation-page-lane51__eyebrow">Conversation workspace</p>
          <h2>Checking access</h2>
          <p role="status">Checking conversation permissions…</p>
          <Link to="/conversations">Back to conversations</Link>
        </div>
      </section>
    );
  }
  if (authorizationView === 'unavailable') {
    return (
      <section className="grid conversation-page-lane51" aria-label="Conversation">
        <div className="card full conversation-shell conversation-page-lane51__access">
          <p className="conversation-page-lane51__eyebrow">Conversation workspace</p>
          <h2>Conversation unavailable</h2>
          <p role="alert">
            This conversation is unavailable or you no longer have access to it.
          </p>
          <Link to="/conversations">Back to conversations</Link>
        </div>
      </section>
    );
  }

  return (
    <section className="grid conversation-page-lane51" aria-label="Conversation">
      {apiClient.mode === 'tool-browser' && <div className="card full" role="status">
        <p>Original Mission Control conversation UI, limited to this tool token’s exact operations.</p>
        <p>Select an actor-owned native Fence model to send. Queue mutation, approvals, credentials, attachments and terminal/WebSocket control are not delegated. History is limited to the most recent 50 messages.</p>
      </div>}
      {apiClient.mode === 'tool-browser' && conversationAuthorizationReady && <ToolBrowserActivity
        key={`${conversationId}:${conversationAuthorizationEpoch}`} conversationId={conversationId}
        apiClient={apiClient} runs={browserOperations?.runs_read === true}
        logs={browserOperations?.logs_read === true} artifacts={browserOperations?.artifacts_read === true}
        onExpired={async () => { handleConversationAuthorizationLost(); }}
      />}
      <div className="card full conversation-shell">
        <ConversationHeader
          appId={appId}
          conversation={conversation}
          editingTitle={editingTitle}
          editingTitleValue={editingTitleValue}
          editingTitleBusy={editingTitleBusy}
          effectiveConversationStatus={effectiveConversationStatus}
          isArchived={isArchived}
          gitlabInfo={gitlabInfo}
          multiGitlabApps={multiGitlabApps}
          gitlabBusy={gitlabBusy}
          gitlabError={gitlabError}
          squashOnMerge={squashOnMerge}
          runnerStateMessage={showRunnerBanner ? runnerState.message : ''}
          runnerProgressLabel={runnerProgressLabel}
          interactionModeBusy={interactionModeBusy}
          canManageConversation={controls.canManageConversation}
          canEditTitle={canEditTitle}
          canManageSecrets={controls.canManageSecrets}
          canManageTools={controls.canManageTools}
          canUseDelegatedExecutionGrants={canUseDelegatedExecutionGrants}
          canReadCode={controls.canReadCode}
          canAttachTerminal={controls.canAttachTerminal}
          canProposeCodeChanges={controls.canProposeCodeChanges}
          canMergeCodeChanges={controls.canMergeCodeChanges}
          onTitleChange={setEditingTitleValue}
          onTitleKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void saveEditedTitle();
            } else if (event.key === 'Escape') {
              cancelEditingTitle();
            }
          }}
          onSaveTitle={() => void saveEditedTitle()}
          onStartEditingTitle={startEditingTitle}
          onCancelEditingTitle={cancelEditingTitle}
          onShowSecrets={() => setShowConversationSecrets(true)}
          onShowToolAccess={apiClient.mode === 'tool-browser' ? () => setShowToolAccess(true) : openToolAccess}
          onShowTerminal={showTerminalPanel}
          onToggleArchive={toggleArchive}
          onCreateMergeRequest={() => void createMergeRequest()}
          onMergeMergeRequest={() => void mergeMergeRequest(squashOnMerge)}
          onSetSquashOnMerge={setSquashOnMerge}
          onShowRunnerDetails={() => {
            ensureRunnerSidebarVisible(streamingRun?.message_id ?? activeRun?.message_id);
          }}
          onSetInteractionMode={(mode) => void setInteractionMode(mode)}
          pipelineStatusClass={pipelineStatusClass}
          mrStatusClass={mrStatusClass}
        />
        {conversationError && (
          <div role="alert" className="conversation-page-lane51__error">
            <ErrorBanner message={conversationError} onDismiss={() => setConversationError(null)} />
          </div>
        )}
        <div
          ref={conversationBodyRef}
          className={conversationBodyClassName}
          style={conversationBodyStyle}
        >
          <div
            className="conversation-thread"
            ref={threadRef}
            role="region"
            aria-label="Conversation messages"
            tabIndex={0}
            aria-live="polite"
          >
          {conversation?.interaction_mode === 'interactive_agent' && (
            <InteractiveAgentFeed
              items={conversationProjection?.items ?? []}
              loading={conversationProjectionLoading}
            />
          )}
          {(loadingOlderMessages || hasMoreMessages) && (
            <div className="message-loading-indicator">
              {loadingOlderMessages ? (
                <span role="status">Loading older messages…</span>
              ) : (
                <button
                  type="button"
                  className="ghost compact"
                  onClick={() => void loadOlderMessages()}
                >
                  Load older messages
                </button>
              )}
            </div>
          )}
          <ConversationMessageList
            displayMessages={displayMessages}
            assignedAssistantIds={assignedAssistantIds}
            queueByMessageId={queueByMessageId}
            runByMessageId={runByMessageId}
            assistantByUserId={assistantByUserId}
            streamingRun={streamingRun}
            assistantText={assistantText}
            activeRunActivityTree={activeRunActivityTree}
            activityTreeByMessageId={activityTreeByMessageId}
            activeLivePreview={activeLivePreview}
            runnerStateMessage={runnerState.message}
            queueSpanByMessageId={queueSpanByMessageId}
            queueOrderByMessageId={queueOrderByMessageId}
            autopilotMessageIds={autopilotMessageIds}
            messagePreviewById={messagePreviewById}
            selectedActivityMessageId={selectedActivityMessageId}
            queueReorderBusy={queueReorderBusy}
            editingMessageId={editingMessageId}
            editingContent={editingContent}
            editingBusy={editingBusy}
            actorUserId={currentConversationAccess?.actor_user_id ?? null}
            canRespond={controls.canRespond}
            canMutateOwnTurns={apiClient.mode !== 'tool-browser' && controls.canRespond}
            canCancelOwnTurns={apiClient.mode === 'tool-browser' ? browserOperations?.messages_cancel === true : undefined}
            canManageConversation={controls.canManageConversation}
            canInspectRuntime={controls.canRunWorkspace}
            canReadArtifacts={controls.canReadArtifacts}
            canPublishArtifacts={controls.canPublishArtifacts}
            onEditingContentChange={setEditingContent}
            onEditKeyDown={handleEditKeyDown}
            onCancelEditing={cancelEditing}
            onSaveEditedMessage={(messageId) => void saveEditedMessage(messageId)}
            onCopyMessageContent={(content) => void copyMessageContent(content)}
            onOpenFixBug={(messageId, queueSpanId) => {
              setFixBugMessageId(messageId);
              setFixBugSpanId(queueSpanId);
              setFixBugOpen(true);
            }}
            onOpenMessageActivitySidebar={(messageId, runId) =>
              void openMessageActivitySidebar(messageId, runId)
            }
            onMoveQueuedMessage={(messageId, direction) =>
              void moveQueuedMessage(messageId, direction)
            }
            onToggleQueuedMessageHold={(itemId, manualHold) =>
              void toggleQueuedMessageHold(itemId, manualHold)
            }
            onStartQueuedMessage={(itemId) => void startQueuedMessage(itemId)}
            onCancelQueue={(itemId) => void cancelQueue(itemId)}
            onAllowPipelineWait={(itemId) => void allowPipelineWait(itemId)}
            onStopPipelineWait={(itemId) => void stopPipelineWait(itemId)}
            onStartEditingMessage={startEditing}
            onPreviewAttachment={(attachment) => void previewAttachment(attachment)}
            onDownloadAttachment={(attachment) => void downloadAttachment(attachment)}
            onDeleteAttachment={requestDeleteAttachment}
            messageStatusBadgeClass={messageStatusBadgeClass}
            messageStatusLabel={messageStatusLabel}
          />
          {eventsError && (
            <div className="message-bubble message-bubble--system">
              <div className="message-meta">
                <span>Live connection</span>
              </div>
              <div className="message-error">{eventsError}</div>
            </div>
          )}
          {controls.canAttachTerminal && <div
            ref={terminalPanelRef}
            className="conversation-terminal-anchor"
            role="region"
            aria-label="Terminal workspace"
            tabIndex={-1}
          >
            <SessionTerminalPanel
              apiClient={apiClient}
              conversationId={conversationId}
              sessions={sessions}
              canStartSessions={controls.canStartTerminalSessions}
              selectedSessionId={terminalSelectedSessionId}
              onSelectedSessionChange={setTerminalSelectedSessionId}
              resumeSessionId={terminalResumeSessionId}
              onAttachedSessionChange={setTerminalResumeSessionId}
              onClearResumeSession={() => setTerminalResumeSessionId(null)}
              sessionsFreshForAuthorization={
                terminalSessionSnapshotGeneration === terminalAuthorizationGeneration
              }
              canAutomaticallyReconnect={() => terminalAutoReconnectAllowedRef.current}
              onRefreshSessions={() => void loadSessions()}
              setError={setError}
            />
          </div>}
          {activeRun?.status === 'error' &&
            activeRun.error &&
            !activeRunErrorRenderedInThread &&
            runErrorDismissedId !== activeRun.id && (
              <div className="message-bubble message-bubble--system">
                <div className="message-meta">
                  <span>Run error</span>
                </div>
                <div className="message-error">{activeRun.error}</div>
                <button
                  type="button"
                  className="ghost"
                  onClick={() => setRunErrorDismissedId(activeRun.id)}
                >
                  Dismiss
                </button>
              </div>
            )}
          {approvalRequest && (
            <div className="message-bubble message-bubble--system">
              <div className="message-meta">
                <span>{approvalRequest.title}</span>
              </div>
              {approvalRequest.detail && (
                <div className="message-content">{approvalRequest.detail}</div>
              )}
              {controls.canRunWorkspace && hasPromptOptions && (
                <div className="row">
                  {approvalRequest.options?.map((option) => (
                    <button
                      key={`${option.value}:${option.label}`}
                      type="button"
                      disabled={approvalBusy}
                      onClick={() => submitApproval({ response: option.value })}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
              {controls.canRunWorkspace && !hasPromptOptions && approvalRequest.kind !== 'input' && (
                <div className="row">
                  <button
                    type="button"
                    disabled={approvalBusy}
                    onClick={() => submitApproval({ decision: 'approve' })}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    disabled={approvalBusy}
                    onClick={() => submitApproval({ decision: 'deny' })}
                  >
                    Deny
                  </button>
                </div>
              )}
              {controls.canRunWorkspace && allowFreeTextResponse && (
                <div className="stack">
                  <input
                    type="text"
                    value={approvalResponseValue}
                    placeholder={approvalRequest.placeholder ?? 'Type response'}
                    onChange={(event) =>
                      setApprovalResponseDraft({
                        requestKey: approvalRequestKey,
                        value: event.target.value
                      })
                    }
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter') {
                        return;
                      }
                      event.preventDefault();
                      const response = approvalResponseValue.trim();
                      if (!response || approvalBusy) {
                        return;
                      }
                      void submitApproval({ response });
                    }}
                    disabled={approvalBusy}
                  />
                  <div className="row">
                    <button
                      type="button"
                      disabled={approvalBusy || !approvalResponseValue.trim()}
                      onClick={() => {
                        const response = approvalResponseValue.trim();
                        if (!response) {
                          return;
                        }
                        void submitApproval({ response });
                      }}
                    >
                      {approvalRequest.submit_label ?? 'Send response'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {controls.canManageConversation && toolApprovals.map((approval) => {
            // The list itself requires conversation:manage; an approval
            // decision additionally requires executor:manage. The backend
            // remains authoritative for the request actor's approval.
            const canRespondToApproval = controls.canManageTools;
            return (
            <div key={approval.id} className={`message-bubble message-bubble--system ${approval.sandbox_id ? 'sandbox-approval-card' : ''}`}>
              <div className="message-meta">
                <span>{approval.title}</span>
                <span>{approval.server.name}</span>
              </div>
              {approval.sandbox_id && (
                <div className="sandbox-approval-badge">
                  Sandbox Build Request
                </div>
              )}
              {approval.detail && <div className="message-content">{approval.detail}</div>}
              {approval.cmd && <pre className="message-content"><code>{approval.cmd}</code></pre>}
              {approval.path && <pre className="message-content"><code>{approval.path}</code></pre>}
              {approval.preview && (
                <textarea className="log-box" rows={8} readOnly value={approval.preview} />
              )}
              {canRespondToApproval && <div className="row">
                <button
                  type="button"
                  disabled={toolApprovalBusyKey !== null}
                  onClick={() => void respondToToolApproval(approval.id, { decision: 'approve' })}
                >
                  Approve
                </button>
                {approval.approval_kind === 'ops_exec' && (
                  <button
                    type="button"
                    disabled={toolApprovalBusyKey !== null}
                    onClick={() =>
                      void respondToToolApproval(approval.id, {
                        decision: 'approve',
                        remember: true
                      })
                    }
                  >
                    Approve & Remember
                  </button>
                )}
                <button
                  type="button"
                  className="secondary"
                  disabled={toolApprovalBusyKey !== null}
                  onClick={() => void respondToToolApproval(approval.id, { decision: 'deny' })}
                >
                  Reject
                </button>
              </div>}
            </div>
            );
          })}
          {sandboxes.length > 0 && (
            <div className="message-bubble message-bubble--system sandbox-panel">
              <div className="message-meta">
                <span>ML Sandboxes</span>
                <span>{sandboxes.length} sandbox{sandboxes.length !== 1 ? 'es' : ''}</span>
              </div>
              {sandboxes.map((sandbox) => {
                const runs = sandboxRuns.filter((r) => r.sandbox_id === sandbox.id);
                const isActive = ['pending', 'building', 'built'].includes(sandbox.build_status);
                const statusClass = `sandbox-status sandbox-status--${sandbox.build_status}`;
                return (
                  <div key={sandbox.id} className="sandbox-card">
                    <div className="sandbox-card__header">
                      <span className="sandbox-card__title">
                        {sandbox.dockerfile_path}
                        {sandbox.branch && <span className="sandbox-card__branch"> ({sandbox.branch})</span>}
                      </span>
                      <span className={statusClass}>{sandbox.build_status}</span>
                    </div>
                    <div className="sandbox-card__details">
                      <span>Server: {sandbox.server.name}</span>
                      <span>Runtime: {sandbox.runtime_policy}</span>
                      <span>Mount: {sandbox.mount_profile}</span>
                      {sandbox.image_tag && <span>Image: {sandbox.image_tag}</span>}
                    </div>
                    {sandbox.build_status === 'built' && runs.length > 0 && (
                      <div className="sandbox-card__runs">
                        {runs.slice(0, 5).map((run) => (
                          <div key={run.id} className={`sandbox-run sandbox-run--${run.status}`}>
                            <div className="sandbox-run__header">
                              <span className="sandbox-run__command">
                                {run.command.length > 80 ? run.command.slice(0, 80) + '...' : run.command}
                              </span>
                              <span className={`sandbox-run__status sandbox-run__status--${run.status}`}>
                                {run.status}
                                {run.exit_code != null && ` (${run.exit_code})`}
                              </span>
                            </div>
                            {run.log_tail && (
                              <pre className="sandbox-run__log">{run.log_tail.slice(-500)}</pre>
                            )}
                            {run.error && <div className="sandbox-run__error">{run.error}</div>}
                          </div>
                        ))}
                        {runs.length > 5 && (
                          <div className="sandbox-card__more">
                            +{runs.length - 5} more run{runs.length - 5 !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    )}
                    {isActive && sandbox.build_status !== 'built' && (
                      <div className="sandbox-card__progress">
                        {sandbox.build_status === 'building' && 'Building image...'}
                        {sandbox.build_status === 'pending' && 'Waiting to build...'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {conversation?.interaction_mode !== 'interactive_agent' &&
            messages.length === 0 &&
            !assistantText.trim() &&
            activeOutputEvents.length === 0 &&
            activeUpdateEvents.length === 0 && (
            <div className="conversation-page-lane51__thread-state" role="status">
              <strong>{loadingMessages || !initialMessagesLoaded ? 'Loading messages…' : 'No messages to display'}</strong>
              <p>
                {loadingMessages || !initialMessagesLoaded
                  ? 'Conversation history is being retrieved.'
                  : conversationError
                    ? 'See the error above for details.'
                    : isArchived
                      ? 'This conversation is archived.'
                      : controls.canRespond
                        ? 'Use the composer below to write a message.'
                        : 'Messages will appear here when available.'}
              </p>
            </div>
          )}
          </div>
          <ConversationActivitySidebar
            apiClient={apiClient}
            show={controls.canRunWorkspace && showRunnerSidebar}
            conversationId={conversationId}
            selectedActivityQueueItem={selectedActivityQueueItem}
            selectedActivityTree={selectedActivityTree}
            selectedActivityLoading={selectedActivityLoading}
            selectedActivityLoadingOlder={selectedActivityLoadingOlder}
            selectedActivityRunId={selectedActivityRunId}
            selectedActivityRunCwd={selectedActivityRun?.cwd ?? null}
            effectiveSelectedActivityMessageId={effectiveSelectedActivityMessageId}
            queueActivityLoading={selectedActivityQueueLoading}
            selectedPreparationActivityGrouped={selectedPreparationActivityGrouped}
            selectedQueueActivityGrouped={selectedQueueActivityGrouped}
            messagePreviewById={messagePreviewById}
            expandedActivityStages={expandedActivityStages}
            expandedActivityCommands={expandedActivityCommands}
            visibleActivityStagesByMessage={visibleActivityStagesByMessage}
            visibleActivityCommandsByStage={visibleActivityCommandsByStage}
            visibleActivityOutputLinesByCommand={visibleActivityOutputLinesByCommand}
            loadingOlderCommandOutput={loadingOlderCommandOutput}
            copyingRawCommandOutput={copyingRawCommandOutput}
            activityStagesPage={ACTIVITY_STAGES_PAGE}
            activityStageCommandsPage={ACTIVITY_STAGE_COMMANDS_PAGE}
            activityOutputLinesPage={ACTIVITY_OUTPUT_LINES_PAGE}
            onClose={closeRunnerSidebar}
            onStartResize={startRunnerSidebarResize}
            onResize={onRunnerSidebarResize}
            onFinishResize={finishRunnerSidebarResize}
            onResizeKeyDown={handleRunnerSidebarResizeKeyDown}
            onToggleStage={toggleExpandedActivityStage}
            onToggleCommand={toggleExpandedActivityCommand}
            onShowMoreOutput={showMoreLoadedActivityOutput}
            onShowMoreCommands={showMoreActivityCommands}
            onShowMoreStages={showMoreActivityStages}
            onLoadOlderCommandOutput={loadOlderCommandOutput}
            onCopyRawAgentCommandOutput={copyRawAgentCommandOutput}
            onLoadOlderActivityCommands={loadOlderActivityCommands}
            messageVisibleStageCountKey={messageVisibleStageCountKey}
            stageExpandKey={stageExpandKey}
            stageVisibleCommandCountKey={stageVisibleCommandCountKey}
            commandExpandKey={commandExpandKey}
            commandVisibleOutputLineCountKey={commandVisibleOutputLineCountKey}
          />
        </div>
        {showJumpToLatest && (
          <div className="jump-to-latest">
            <button
              type="button"
              className="ghost"
              onClick={() => {
                const el = threadRef.current;
                if (el) {
                  el.scrollTop = el.scrollHeight;
                }
                setShowJumpToLatest(false);
              }}
              aria-label="Jump to newest message"
            >
              Newest
            </button>
          </div>
        )}
        <ConversationComposer
          inputRef={inputRef}
          modelMenuRef={modelMenuRef}
          autopilotMenuRef={autopilotMenuRef}
          draftHistoryMenuRef={draftHistoryMenuRef}
          sending={sending}
          holdQueuedMessages={holdQueuedMessages}
          composerWillQueue={composerWillQueue}
          isArchived={isArchived}
          inputValue={inputValue}
          draftVersions={draftVersions}
          activeDraftVersionId={activeDraftVersionId}
          selectedModelId={selectedModelId}
          modelSelectionRequired={apiClient.mode === 'tool-browser'}
          models={models}
          modelMenuOpen={modelMenuOpen}
          draftHistoryOpen={draftHistoryOpen}
          autopilotBusy={autopilotBusy}
          autopilotMenuOpen={autopilotMenuOpen}
          autopilotMode={autopilotMode}
          pendingAttachments={pendingAttachments}
          attachmentBusy={attachmentBusy}
          canPublishArtifacts={controls.canPublishArtifacts}
          canRespond={controls.canRespond}
          canManageConversation={controls.canManageConversation}
          onSubmit={sendMessage}
          onInputChange={handleComposerInputChange}
          onInputKeyDown={handleInputKeyDown}
          onToggleDraftHistory={() => setDraftHistoryOpen((prev) => !prev)}
          onSelectDraftVersion={handleSelectDraftVersion}
          onSetDraftHistoryOpen={setDraftHistoryOpen}
          onSelectFiles={(files) => void uploadComposerFiles(files)}
          onRemoveAttachment={requestDeleteAttachment}
          onHoldToggle={setHoldQueuedMessages}
          onToggleModelMenu={() => setModelMenuOpen((prev) => !prev)}
          onSelectModel={selectModel}
          onSetModelMenuOpen={setModelMenuOpen}
          onToggleAutopilotMenu={() => setAutopilotMenuOpen((prev) => !prev)}
          onUpdateAutopilotMode={updateAutopilotMode}
        />
      </div>

      <ConversationSecretsModal
        open={showConversationSecrets && controls.canManageSecrets}
        busy={conversationSecretsBusy}
        error={conversationSecretsError}
        opsSecrets={conversationOpsSecrets}
        overridesByKey={conversationSecretOverridesByKey}
        draftKey={conversationSecretDraftKey}
        draftType={conversationSecretDraftType}
        draftValue={conversationSecretDraftValue}
        draftDescription={conversationSecretDraftDescription}
        selectedOpsSecret={selectedConversationOpsSecret}
        onClose={() => setShowConversationSecrets(false)}
        onRefresh={() => loadConversationSecrets()}
        onDismissError={() => setConversationSecretsError(null)}
        onDraftKeyChange={setConversationSecretDraftKey}
        onDraftTypeChange={setConversationSecretDraftType}
        onDraftValueChange={setConversationSecretDraftValue}
        onDraftDescriptionChange={setConversationSecretDraftDescription}
        onResetOverride={(id) => resetConversationSecretOverride(id)}
        onSaveOverride={() => saveConversationSecretOverride()}
      />

      <ConversationToolAccessModal
        open={showToolAccess && (controls.canManageTools || canUseDelegatedExecutionGrants)}
        scopedExecutionGrants={apiClient.mode === 'tool-browser' ? <ToolBrowserGrants
          key={`${conversationId}:${conversationAuthorizationEpoch}`} apiClient={apiClient}
          target={{ kind: 'conversation', id: conversationId }} onExpired={delegatedGrantAuthorityLost}
        /> : undefined}
        busy={toolTokenBusy || execGrantBusy}
        status={toolTokenStatus}
        tokenValue={toolTokenValue}
        scopes={toolTokenScopes}
        execGrants={execGrants}
        execGrantKey={execGrantKey}
        execGrantName={execGrantName}
        execGrantDurationAmount={execGrantDurationAmount}
        execGrantDurationUnit={execGrantDurationUnit}
        execGrantPrivileged={execGrantPrivileged}
        execGrantExecutorRefs={execGrantExecutorRefs}
        execGrantMaxUses={execGrantMaxUses}
        onExecGrantNameChange={setExecGrantName}
        onExecGrantDurationAmountChange={setExecGrantDurationAmount}
        onExecGrantDurationUnitChange={setExecGrantDurationUnit}
        onExecGrantPrivilegedChange={setExecGrantPrivileged}
        onExecGrantExecutorRefsChange={setExecGrantExecutorRefs}
        onExecGrantMaxUsesChange={setExecGrantMaxUses}
        onRefreshExecGrants={loadExecGrants}
        onCreateExecGrant={createExecGrant}
        onRevokeExecGrant={revokeExecGrant}
        onExtendExecGrant={extendExecGrant}
        onCopyExecGrantKey={copyExecGrantKey}
        onClose={() => setShowToolAccess(false)}
        onRefresh={loadToolTokenStatus}
        onCopyToken={copyToolToken}
        onToggleScope={toggleToolScope}
        onCreateToken={createToolToken}
        onRevokeToken={revokeToolToken}
      />

      <Dialog
        open={Boolean(attachmentDeleteTarget)}
        title="Delete attachment"
        size="small"
        onClose={() => {
          if (!attachmentDeleteBusy) {
            setAttachmentDeleteTarget(null);
          }
        }}
        footer={
          <>
            <button
              type="button"
              className="secondary"
              disabled={attachmentDeleteBusy}
              onClick={() => setAttachmentDeleteTarget(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="danger"
              disabled={attachmentDeleteBusy}
              onClick={() => void confirmDeleteAttachment()}
            >
              {attachmentDeleteBusy ? 'Deleting...' : 'Delete forever'}
            </button>
          </>
        }
      >
        {attachmentDeleteTarget && (
          <div className="dialog-body attachment-delete-confirm">
            <div className="attachment-delete-confirm__file">
              <span className="attachment-delete-confirm__name">
                {attachmentDeleteTarget.filename}
              </span>
              <span className="attachment-delete-confirm__path">
                {attachmentDeleteTarget.workspace_path}
              </span>
            </div>
            <p>
              This permanently removes the file from the database and the conversation workspace.
              It cannot be restored from this conversation after deletion.
            </p>
          </div>
        )}
      </Dialog>

      <FixBugDialog
        open={fixBugOpen && controls.canManageConversation}
        onClose={() => setFixBugOpen(false)}
        apiClient={apiClient}
        models={models}
        apps={apps}
        defaultAppId={apps.find((app) => app.is_self)?.id}
        defaultTitle="Fix Bug"
        defaultPrompt={`Please investigate and fix the issue shown in the logs.

You can use the MCP tools to query logs:
${fixBugSpanId ? `- Use get_logs with the span_id "${fixBugSpanId}" to investigate` : `- Use get_logs with the message_id "${fixBugMessageId}" to investigate`}
- Use get_log_detail to get full details of specific log entries

Focus on understanding what went wrong from the error messages, relevant traces and spans, and the root cause.

${FIX_BUG_WORKFLOW_INSTRUCTIONS}`}
        onCreated={(created) => navigate(`/conversations/${created.id}`)}
        setError={setError}
      />
    </section>
  );
}
